/**
 * Translates a setup-revision status into what navigation and profile controls
 * may do. This is the only place that decides whether a destination commits,
 * prompts, or is refused — the guard and the UI both read it.
 */

import type { NavigationDestinationKind } from './navigationIntent';
import { isSetupRevisionInFlight, type SetupRevisionStatus } from './revisionState';

export interface NavigationPolicy {
  /** Later workflow steps and workspace destinations are not reachable. */
  forwardLocked: boolean;
  /** A save/discard decision is owed before anything commits. */
  requiresUnsavedPrompt: boolean;
  /** A checkpoint operation is running; controls render as busy and disabled. */
  controlsBusy: boolean;
  /** The mapping workspace (tabs, Quick Fill, Validation, Export) is closed. */
  workspaceBlocked: boolean;
}

export const getNavigationPolicy = (status: SetupRevisionStatus): NavigationPolicy => ({
  forwardLocked: status !== 'ready',
  requiresUnsavedPrompt: status === 'dirty',
  controlsBusy: isSetupRevisionInFlight(status),
  workspaceBlocked: status !== 'ready',
});

export type NavigationDecision = 'commit' | 'prompt' | 'block';

/**
 * Backward navigation stays available whenever nothing is in flight: the user
 * must always be able to step back to the screen that owns the problem. It
 * still goes through the guard, so it never implies a verified target.
 */
export const resolveNavigationDecision = (
  status: SetupRevisionStatus,
  kind: NavigationDestinationKind
): NavigationDecision => {
  const policy = getNavigationPolicy(status);

  if (policy.controlsBusy) {
    return 'block';
  }

  if (policy.requiresUnsavedPrompt) {
    return 'prompt';
  }

  if (kind === 'setupBack') {
    return 'commit';
  }

  return policy.forwardLocked ? 'block' : 'commit';
};
